import styled, { css, keyframes } from 'styled-components';

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const driftBg = keyframes`
  0%   { transform: translate3d(0,0,0); }
  50%  { transform: translate3d(-20px,-10px,0); }
  100% { transform: translate3d(0,0,0); }
`;

export const PageContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 100vh;
  width: 100vw;
  background: #f8fafc;
  font-family: 'Plus Jakarta Sans', sans-serif;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

export const LeftPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #f8fafc;
  position: relative;
  overflow-y: auto;

  @media (max-width: 968px) {
    min-height: 100vh;
  }
`;

export const FormWrap = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const BrandBlock = styled.div<{ $delay?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0;
  animation: ${slideUp} 0.55s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  img {
    width: 160px;
    height: auto;
    margin-bottom: 12px;
  }

  h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.2;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
    line-height: 1.5;
  }
`;

export const InfoCard = styled.div<{ $delay?: number }>`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  opacity: 0;
  animation: ${slideUp} 0.55s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  .heading {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  .heading svg {
    color: #006633;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    color: #475569;
    font-size: 13px;
    line-height: 1.55;
    font-weight: 500;
  }

  li + li {
    margin-top: 4px;
  }

  p {
    margin: 0;
    color: #475569;
    font-size: 13px;
    line-height: 1.55;
    font-weight: 500;
  }

  strong {
    color: #0f172a;
    font-weight: 700;
  }
`;

export const FormBlock = styled.form<{ $delay?: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  opacity: 0;
  animation: ${slideUp} 0.55s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    letter-spacing: -0.01em;
  }

  .error {
    color: #b91c1c;
    font-size: 12px;
    font-weight: 500;
    margin-top: 2px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const InputWrapper = styled.div<{ hasError?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;

  .input-icon {
    position: absolute;
    left: 14px;
    color: ${({ hasError }) => (hasError ? '#b91c1c' : '#94a3b8')};
    font-size: 16px;
    pointer-events: none;
    transition: color 0.15s ease;
  }

  input,
  textarea {
    width: 100%;
    padding: 0 14px 0 42px;
    border: 1px solid ${({ hasError }) => (hasError ? '#fca5a5' : '#e2e8f0')};
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    outline: none;

    &::placeholder {
      color: #94a3b8;
    }

    &:hover {
      border-color: ${({ hasError }) => (hasError ? '#fca5a5' : '#cbd5e1')};
    }

    &:focus {
      border-color: ${({ hasError }) => (hasError ? '#b91c1c' : '#006633')};
      box-shadow: 0 0 0 3px
        ${({ hasError }) =>
          hasError ? 'rgba(185, 28, 28, 0.1)' : 'rgba(0, 102, 51, 0.12)'};
    }
  }

  input {
    height: 44px;
  }

  textarea {
    padding: 12px 14px 12px 42px;
    min-height: 110px;
    resize: vertical;
    line-height: 1.5;
  }

  ${({ hasError }) =>
    hasError &&
    css`
      input:focus + .input-icon,
      textarea:focus + .input-icon {
        color: #b91c1c;
      }
    `}
`;

export const SubmitButton = styled.button`
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 6px 16px rgba(185, 28, 28, 0.2);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(185, 28, 28, 0.28);
    filter: brightness(1.05);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Helper = styled.div<{ $delay?: number }>`
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  font-weight: 500;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  a {
    color: #006633;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      color: #00451f;
      text-decoration: underline;
    }
  }
`;

export const RightPanel = styled.div<{ $delay?: number }>`
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #006633 0%, #00874a 55%, #00a65a 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #ffffff;

  opacity: 0;
  animation: ${fadeIn} 0.7s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  @media (max-width: 968px) {
    display: none;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.35;
    animation: ${driftBg} 8s ease-in-out infinite;
  }
  &::before {
    width: 360px;
    height: 360px;
    background: #f38725;
    top: -80px;
    right: -80px;
  }
  &::after {
    width: 460px;
    height: 460px;
    background: #00a65a;
    bottom: -120px;
    left: -100px;
    animation-delay: 2s;
  }

  .pattern {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(255, 255, 255, 0.08) 1px,
      transparent 1px
    );
    background-size: 22px 22px;
    pointer-events: none;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;

  img {
    width: 240px;
    height: auto;
    filter: drop-shadow(0 12px 32px rgba(0, 0, 0, 0.25));
  }

  h2 {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.2;
  }

  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.82);
    font-weight: 500;
  }
`;

export const HeroBullets = styled.ul`
  position: relative;
  z-index: 1;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 420px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    border-radius: 12px;
    padding: 12px 14px;
    color: rgba(255, 255, 255, 0.92);
    font-size: 13px;
    font-weight: 500;
  }

  li svg {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.95);
  }
`;
